/**
 * Legacy Button — maps onto ui/button (D-124).
 * primary → inverted default; secondary → glass outline.
 */

import React from 'react';
import { Button as UiButton } from '@/components/ui/button';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const VARIANT: Record<NonNullable<ButtonProps['variant']>, 'default' | 'outline' | 'destructive' | 'ghost'> = {
  primary: 'default',
  secondary: 'outline',
  outline: 'outline',
  destructive: 'destructive',
  ghost: 'ghost',
};

const SIZE: Record<NonNullable<ButtonProps['size']>, 'sm' | 'default' | 'lg'> = {
  sm: 'sm',
  md: 'default',
  lg: 'lg',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {
    return (
      <UiButton
        className={className}
        variant={VARIANT[variant]}
        size={SIZE[size]}
        ref={ref}
        disabled={disabled || isLoading}
        data-ui-id={`button-${variant}-${size}`}
        data-ui-props={JSON.stringify({
          type: 'button',
          component: 'Button',
          variant,
          size,
          disabled: disabled || isLoading,
          isLoading,
          hasLeftIcon: !!leftIcon,
          hasRightIcon: !!rightIcon,
        })}
        {...props}
      >
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {!isLoading && leftIcon && <span className="mr-0">{leftIcon}</span>}
        {children}
        {!isLoading && rightIcon && <span className="ml-0">{rightIcon}</span>}
      </UiButton>
    );
  },
);

Button.displayName = 'Button';

export default Button;
